import { useEffect, useState } from "react";
import { Database, Server, Zap, CheckCircle2, XCircle, Clock, Mail, Send } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

function TestEmailPanel({ sender }) {
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!to.includes("@")) { toast.error("Enter a valid email"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/email/test", { to });
      if (data.result?.status === "sent") toast.success(`Sent via Resend (${sender}) — check the inbox`);
      else if (data.result?.status === "skipped") toast.warning("Log-only mode — add RESEND_API_KEY to send");
      else toast.error(data.message || "Delivery error");
    } catch (e) { toast.error("Could not send test email"); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-3 flex gap-2" data-testid="admin-email-test">
      <Input value={to} onChange={(e) => setTo(e.target.value)} data-testid="email-test-to"
        placeholder="Send a test email to…" className="bg-[#0d0d0f] border-white/10 text-white h-9 max-w-xs" />
      <Button onClick={send} disabled={busy} data-testid="email-test-send"
        className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full h-9 text-xs">
        <Send className="w-3.5 h-3.5 mr-1" /> {busy ? "Sending…" : "Send test"}
      </Button>
    </div>
  );
}

function fmtUptime(s) {
  if (s == null) return "—";
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

export default function AdminSystem() {
  const [d, setD] = useState(null);
  useEffect(() => {
    const load = () => api.get("/admin/system").then((r) => setD(r.data)).catch(() => {});
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, []);

  const dbOk = d?.database?.connected;
  const emailLive = d?.email?.live;
  return (
    <div className="space-y-4" data-testid="admin-system">
      <div className={`rounded-2xl border p-5 ${emailLive ? "border-emerald-500/25 bg-emerald-500/[0.04]" : "border-amber-500/25 bg-amber-500/[0.04]"}`} data-testid="admin-email-status">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${emailLive ? "bg-emerald-500/15" : "bg-amber-500/15"}`}>
              <Mail className={`w-5 h-5 ${emailLive ? "text-emerald-400" : "text-amber-400"}`} />
            </div>
            <div>
              <div className="text-white font-medium">Email delivery</div>
              <div className="text-xs text-[#71717A]">{d?.email?.provider || "Resend"} · Sender {d?.email?.sender || "—"}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${emailLive ? "bg-emerald-400 animate-pulse" : "bg-amber-400"}`} />
            <span className={`text-xs font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full ${emailLive ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-500/15 text-amber-400"}`} data-testid="admin-email-status-badge">
              {emailLive ? "Live — sending real emails" : "Log-only — not sending"}
            </span>
          </div>
        </div>
        {!emailLive && (
          <p className="text-xs text-amber-300/90 mt-3" data-testid="admin-email-status-hint">
            Emails are logged instead of sent. Add <code className="text-amber-200">RESEND_API_KEY</code> and verify your sender domain in Resend to go live — no code changes needed.
          </p>
        )}
        <TestEmailPanel sender={d?.email?.sender || "sender"} />
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <div className="rounded-2xl border border-white/8 bg-[#121214] p-5" data-testid="admin-system-db">
          <div className="flex items-center justify-between mb-3"><Database className="w-5 h-5 text-[#D4AF37]" />
            {dbOk ? <CheckCircle2 className="w-5 h-5 text-emerald-400" /> : <XCircle className="w-5 h-5 text-red-400" />}</div>
          <div className="text-white font-medium">Database</div>
          <div className="text-xs text-[#71717A] mt-1">{dbOk ? `Connected · ${d?.database?.latency_ms}ms` : "Disconnected"}</div>
        </div>
        <div className="rounded-2xl border border-white/8 bg-[#121214] p-5">
          <div className="flex items-center justify-between mb-3"><Server className="w-5 h-5 text-[#D4AF37]" /><CheckCircle2 className="w-5 h-5 text-emerald-400" /></div>
          <div className="text-white font-medium">API Server</div>
          <div className="text-xs text-[#71717A] mt-1 flex items-center gap-1"><Clock className="w-3 h-3" /> Uptime {fmtUptime(d?.server?.uptime_seconds)}</div>
        </div>
        <div className="rounded-2xl border border-white/8 bg-[#121214] p-5">
          <div className="flex items-center justify-between mb-3"><Zap className="w-5 h-5 text-[#D4AF37]" /><CheckCircle2 className="w-5 h-5 text-emerald-400" /></div>
          <div className="text-white font-medium">Environment</div>
          <div className="text-xs text-[#71717A] mt-1 capitalize">{d?.server?.env || "—"}</div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-white/8 bg-[#121214] p-5">
          <h3 className="text-white font-medium mb-4">Integrations</h3>
          <div className="space-y-2.5">
            {Object.entries(d?.integrations || {}).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between text-sm">
                <span className="text-[#A1A1AA] capitalize">{k.replace(/_/g, " ")}</span>
                <span className={`text-xs px-2.5 py-1 rounded-full ${String(v).includes("log-only") ? "bg-amber-500/15 text-amber-400" : "bg-emerald-500/15 text-emerald-400"}`}>{v}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-2xl border border-white/8 bg-[#121214] p-5">
          <h3 className="text-white font-medium mb-4">Data collections</h3>
          <div className="grid grid-cols-2 gap-x-6 gap-y-2">
            {Object.entries(d?.collections || {}).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between text-sm">
                <span className="text-[#A1A1AA]">{k.replace(/_/g, " ")}</span>
                <span className="text-white">{v}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <p className="text-xs text-[#52525B]">Auto-refreshes every 15s · Server time {d?.server?.time ? new Date(d.server.time).toLocaleTimeString() : "—"}</p>
    </div>
  );
}
